<template>
    <div>
        <div id="order-summary" role="region" aria-labelledby="order-summary-heading">
            <h3 id="order-summary-heading" :tabindex="errors ? 0 : -1">
                {{ errors ? lang.get('event_strings.forms.stripe_order_errors') : lang.get('event_strings.forms.stripe_order_summary') }}
            </h3>

            <template v-if="!errors">
                <ul class="order-summary-list">
                    <li v-for="posItem in order.itemList" class="container-fluid shopping-cart-item"
                        :key="posItem.id" :data-id="posItem.id">
                        <div class="row">
                            <div class="col-md-10 col-sm-8 col-7">
                                <h4 class="shopping-cart-item-name">{{ posItem.name }}</h4>
                                <p class="shopping-cart-item-descript">{{ posItem.description }}</p>
                            </div>
                            <div class="col-md-2 col-sm-4 col-5">
                                <h4 class="text-right">
                                    <span class="visually-hidden">{{ posItem.name }} Price:</span>
                                    ${{ posItem.price }}
                                </h4>
                            </div>
                        </div>
                    </li>

                    <li v-if="Object.keys(order.couponList).length > 0"
                        class="container-fluid total checkout-item-subtotal">
                        <div class="row">
                            <div class="col-md-10 col-sm-8 col-7">
                                <h4 class="sub-total-label">{{ lang.get('event_strings.forms.stripe_item_subtotal') }}:
                                </h4>
                            </div>
                            <div class="col-md-2 col-sm-4 col-5">
                                <h4 class="sub-total-cost text-right">
                                    ${{ parseFloat(order.itemSubtotal).toFixed(2) }}</h4>
                            </div>
                        </div>
                    </li>

                    <li v-if="Object.keys(order.couponList).length > 0"
                        class="container-fluid line-item checkout-coupon-list">
                        <div class="row">
                             <div class="col-md-8 col-sm-6 col-7">
                                </div>
                             <div class="col-md-4 col-sm-6 col-5 coupon-value">
                                <h4 class="sub-total-label text-right">{{ lang.get('event_strings.forms.stripe_applied_coupons') }}:</h4>
                             </div>
                        </div>
                         <div class="row">
                            <div class="col-md-8 col-sm-6 col-7">
                                </div>
                            <div class="col-md-4 col-sm-6 col-5 coupon-value text-right">
                                <ul class="list-unstyled">
                                    <li v-for="coupon in order.couponList" :key="coupon.code_slug">
                                        {{ coupon.code_slug }}: -{{ coupon.discount_total }}
                                        <button type="button" @click="removeCoupon(coupon.code_slug)"
                                            class="btn btn-clear-search themeSecondaryColor"
                                            :aria-label="`Remove coupon ${coupon.code_slug}`">
                                            <i class="fas fa-times-circle" aria-hidden="true"></i>
                                            </button>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </li>

                    <li v-if="Object.keys(order.couponList).length > 0"
                        class="container-fluid line-item checkout-coupon-subtotal">
                        <div class="row">
                            <div class="col-md-10 col-sm-8 col-7">
                                <h4 class="sub-total-label">{{ lang.get('event_strings.forms.stripe_coupon_subtotal') }}:</h4>
                            </div>
                            <div class="col-md-2 col-sm-4 col-5">
                                <h4 class="sub-total-cost text-right">
                                   ($<span class="visually-hidden">Discount of </span>{{ parseFloat(order.couponSubtotal).toFixed(2) }})
                                </h4>
                            </div>
                        </div>
                    </li>

                    <li class="container-fluid line-item checkout-subtotal">
                         <div class="row">
                            <div class="col-md-10 col-sm-8 col-7">
                                <h4 class="sub-total-label">{{ lang.get('event_strings.forms.stripe_subtotal') }}:</h4>
                            </div>
                            <div class="col-md-2 col-sm-4 col-5">
                                <h4 class="sub-total-cost text-right">${{ parseFloat(order.subtotal).toFixed(2) }}</h4>
                            </div>
                        </div>
                    </li>

                    <li class="container-fluid total checkout-total">
                         <div class="row">
                            <div class="col-md-10 col-sm-8 col-7">
                                <h4 class="total-label">{{ lang.get('event_strings.forms.stripe_total') }}:</h4>
                            </div>
                             <div class="col-md-2 col-sm-4 col-5">
                                <h4 class="total-cost text-right">${{ parseFloat(order.total).toFixed(2) }}</h4>
                            </div>
                        </div>
                    </li>
                </ul>
            </template>

            <template v-else>
                 <div role="alert" aria-atomic="true">
                    <ul class="order-error-list list-unstyled">
                        <li v-for="error in errors" :key="error" class="container-fluid checkout-error">
                             <div class="row">
                                <div class="col-md-12 col-sm-12 col-12">
                                    <p class="error-label">{{ error }}</p>
                                </div>
                            </div>
                        </li>
                    </ul>
                 </div>
            </template>
        </div>

        <div v-if="!(loading || order.total == 0)" class="row" id="coupons">
            <div class="col-6 offset-6 apply-coupons">
                <label for="couponCode" class="visually-hidden">{{ lang.get('event_strings.forms.stripe_discount_code') }}</label>
                <div class="input-group">
                    <input type="text" v-model="couponCode" v-on:keyup.enter="applyCoupon" id="couponCode"
                        class="form-control" :placeholder="lang.get('event_strings.forms.stripe_discount_code')"
                        :aria-label="lang.get('event_strings.forms.stripe_discount_code')"> <div class="input-group-append">
                        <button type="button" @click="applyCoupon" :disabled="!couponCode" class="btn">
                            {{ lang.get('event_strings.forms.stripe_apply_code') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="form-cart-loading" v-if="loading"
             role="status" aria-live="polite" aria-atomic="true">
            <div class="form-cart-spinner-icon fa-5x">
                <i class="fas fa-spinner fa-pulse" aria-hidden="true"></i>
                 <span class="visually-hidden">Loading, please wait...</span>
            </div>
        </div>

        <div :class="(order.total == 0) ? 'hidden' : ''" id="payment-element">
            </div>

    </div>
</template>

<script>

var emptyOrder = {
				'itemIds': [],
				'itemList': [],
				'couponIds': [],
				'couponList': [],
				'itemSubtotal': 0,
				'couponSubtotal': 0,
				'subtotal': 0,
				'clientSurcharge': 0,
				'surcharge': 0,
				'total': 0
			};

export default {

	data: function () {
		return {
			loading: true,
			apiKey: process.env.MIX_STRIPE_KEY,
			intentId: null,
			stripe: null,
			elements: null,
			paymentElement: null,
			regFormResults: null,
			selectedItems: [],
			couponCode: "",
			order: emptyOrder,
			language: document.documentElement.lang,
			complete: false,
			errors: null,
		};
	},
	props: {
		enableDirectCharges: {
			type: Boolean,
			default: false
		},
		stripeAccountId: {
			type: String,
			default: null
		}
	},
	watch: {
		freeItem: {
			handler: function (val) {
				if (val){
					$("body").addClass('stripe-free-item')
					$("body").removeClass('stripe-paid-item')
				}
				else {
					$("body").removeClass('stripe-free-item')
					$("body").addClass('stripe-paid-item')
				}
			},
			immediate: true
		},
		complete: {
			handler: function (val) {
				if (val){
					$("body").addClass('stripe-complete')
					$("body").removeClass('stripe-disabled')
				}
				else {
					$("body").removeClass('stripe-complete')
					$("body").addClass('stripe-disabled')

				}
			},
			immediate: true
		},
		errors: {
			handler: function (val) {
				if (val){
					$("body").addClass('stripe-errors')
					$("body").removeClass('stripe-no-errors')
				}
				else {
					$("body").removeClass('stripe-errors')
					$("body").addClass('stripe-no-errors')
				}
			},
		}
	},
	mounted: function() {
		try{
			this.intentId = document.querySelector('meta[name="stripe-intent"]').content;
		}
		catch(e){
			console.log('stripe intent meta tag not provided: ', e);
		}


		Array.from(document.getElementsByClassName('begin-checkout')).forEach((e) => {
		  	e.addEventListener('click', (e) => {
				this.requestIntent(e, true);
		  });
		});

		document.querySelector('#tab_nav .form-nav__tab:last-of-type').addEventListener('click', (e) => {
			console.log('last-of-ttype query selector');
			console.log(e);
			this.requestIntent(e, true);
		});
		if (typeof submitRegistrationForm !== "undefined") { 
			var regForm = document.querySelector('#registrationForm');
			if (regForm) {
				regForm.addEventListener('submit', (e) => {
					e.preventDefault();
					console.log('submit form');
					  if (isFormValid() && validateConditionalFields()) {
					    setLoader();
					    removeHiddenConditionalFields();
						if (this.regFormResults == null){
							submitRegistrationForm();
						} else {
							this.confirmPurchase(this.regFormResults.intentId);
						}
					  } else {
					    console.log('not valid')
					  }
				});
			}
		}
	},
	computed: {
		couponEndpoint: function(){
			if (window.loggedInVersionOfForm && window.formId){
				return `/forms/${window.formId}/coupon`
			}
			else if (!window.loggedInVersionOfForm && window.formSlug) {
				return `/register/${window.formSlug}/coupon`
			}
			else {
				return '/register/coupon';
			}
		},
		intentEndpoint: function(){
			if (window.loggedInVersionOfForm && window.formId){
				return `/forms/${window.formId}/intent`
			}
			else if (!window.loggedInVersionOfForm && window.formSlug) {
				return `/register/${window.formSlug}/intent`
			}
			else {
				return '/register/intent';
			}
		},
		freeItem: function(){
			return this.order.total == 0;
		}
	},
	methods: {

		processCart: function () {
			this.selectedItems = [];
			this.loading = true;
			var newCost = 0;

			// var selectedItems = document.getElementsByClassName('shopping-item-form');
			var selectedItems = document.querySelectorAll('input.shopping-item-form:checked');
			selectedItems.forEach(function(item) {
				this.selectedItems.push(item.value);
			}, this);
			const bulkItemSelects = document.querySelectorAll('.bulk_pos_items select')
			bulkItemSelects.forEach(function(item) {
				const val = $(item).val();
				if (val == "null") return;
				if (typeof val === 'object') {
					this.selectedItems.push(...val)
				} else {
					this.selectedItems.push(val)
				}
			}, this)
		},
		setLoading: function (loadingValue = true) {
			this.loading = loadingValue;
		},
		requestIntent: function(event, clearCoupons = false) {
			this.processCart();
			//we dont need to update intent if nothing has changed and selected items match those already submitted
			let noUpdates = this.selectedItems.length === this.order.itemIds.length && this.selectedItems.every((value, index) => value === this.order.itemIds[index]);
			
			if (clearCoupons) {
				noUpdates = false;
			}

			if (this.selectedItems.length > 0 && (this.errors || !noUpdates)) {

				Http.post(this.intentEndpoint, {
					items: this.selectedItems, 
					intent_id: this.intentId, 
					lang: document.documentElement.lang,
					clear_coupons: clearCoupons,
				}, function(res) {
					const stripeOptions = {};
					if (this.enableDirectCharges && this.stripeAccountId) {
						stripeOptions.stripeAccount = this.stripeAccountId;
					}
					this.order = res.order;
					console.log(res)
					this.errors = null;
					if (this.stripe === null) {
						console.log('new intent');
						this.intentId = res.id;
						this.stripe = new Stripe(this.apiKey, stripeOptions);
						const options = {
							clientSecret: res.intent,
							country: 'US',
							locale: this.language,
							appearance: {
							  	theme: 'stripe',
								fields: {
									billingDetails: []
								}
							}

						};  
						(async () => {
							
								this.elements = this.stripe.elements(options);
								this.paymentElement = this.elements.create('payment');
								this.paymentElement.mount('#payment-element');

								this.paymentElement.on('ready', function(event) {
								  // Handle ready event
								  this.loading = false;
								}.bind(this));

								this.paymentElement.on('change', function(event) {
									this.complete = event.complete;

								}.bind(this));
								
							
						})();
					} else {
						this.loading = false;
					}
				}.bind(this), function(err) {
				    console.log("ERROR: ", err);
				    this.loading = false;
						if (err.status == 400 && err.responseJSON.status == 'validation_error'){
							console.log(err.responseJSON.errors)
							this.errors = err.responseJSON.errors;
						}
				}.bind(this))

			} else if (this.selectedItems.length == 0) {
				//no items selected, cost is zero, dont require checkout
				this.order = emptyOrder;
				this.loading = false;
			} else {
				//no changes
				this.loading = false;
			}


		},
		regFormReturn: function(data, message) {
			this.regFormResults = data;
			if (this.order.total == 0) {
				$('.modal-title').text('Saved');
	            $('.modal-body').text(message);
	          
	            // if callback success function exists, then trigger it
	            if (typeof registrationFormSubmitted === "function") { 
	              	registrationFormSubmitted(this.regFormResults);
	            } else {
	                $('#myModal').modal('show');
	           	} 
			} else {
				this.confirmPurchase(data.stripe_intent);
			}
		},
		applyCoupon: function(e) {
			e.preventDefault();
			if (!this.couponCode) return;

			let couponInput = document.getElementById('couponCode');
			couponInput.placeholder = window._lang.get('event_strings.forms.stripe_discount_code');
			couponInput.classList.remove('is-invalid');

			Http.post(this.couponEndpoint, {intent_id: this.intentId, coupon_code: this.couponCode}, function(res) {
				this.order = res.order;
			}.bind(this), function(err) {
				if (err.responseJSON.coupon_status === "coupon_dne") {
					couponInput.classList.add('is-invalid');
					couponInput.placeholder = window._lang.get('event_strings.forms.invalid_coupon_code');
				}
				if (err.responseJSON.coupon_status === "coupon_limit") {
					couponInput.classList.add('is-invalid');
					couponInput.placeholder = window._lang.get('event_strings.forms.max_coupons_applied');
				}
				if (err.responseJSON.coupon_status === "coupon_invalid_for_items") {
					couponInput.classList.add('is-invalid');
					couponInput.placeholder = window._lang.get('event_strings.forms.coupon_invalid_for_items');
				}
			    console.log(err);
			})

			
			this.couponCode = "";
		},
		removeCoupon: function(slug) {

			Http.post(this.couponEndpoint, {intent_id: this.intentId, coupon_code: slug, remove: true}, function(res) {
				this.order = res.order;
			}.bind(this), function(err) {
			    console.log(err);
			})

		},
		confirmPurchase: async function(intentId) {

			this.loading = true;
		  var elements = this.elements;
		  const { error } = await this.stripe.confirmPayment({
		    elements,
		    redirect: 'if_required',
		    confirmParams: {
		      return_url: 'https://www.eventfinity.co',
		    },
		  });
		  if (error) {
		  	this.loading = false;
		    // This point will only be reached if there is an immediate error when
		    // confirming the payment. Show error to your customer (for example, payment
		    // details incomplete)
		    const messageContainer = document.querySelector('#error-message');
		    console.log(error.message)
		    //error.message);
            $('.modal-title').text('Unable to Submit Form');
	        $('.modal-body > div').text(error.message);
	        $('#myModal').modal('show');
					if (window.EventBus) {
						EventBus.$emit('stripe-error', {
							message: error.message,
							regResults: this.regFormResults,
							order: this.order,
						});
					}
		  } else {
		  	this.loading = false;
		  	//console.log(intent);
		    // Your customer will be redirected to your `return_url`. For some payment
		    // methods like iDEAL, your customer will be redirected to an intermediate
		    // site first to authorize the payment, then redirected to the `return_url`.

            $('.modal-title').text('Saved');
            //$('.modal-body').text(originalModalText);
          
            // if callback success function exists, then trigger it
            if (typeof registrationFormSubmitted === "function") { 
              	registrationFormSubmitted(this.regFormResults);
            } else {
                $('#myModal').modal('show');
           	} 
		  }
		}
	},
	beforeDestroy(){

	}
}
</script>
<style>
.order-summary-list{
	padding-left: 0;

}
.shopping-cart-item.container-fluid {
	margin-left: 30px;
	padding-right: 45px;
}
.line-item.container-fluid {
	list-style: none;
	padding-top: 5px;
	padding-left: 45px;
}
.total.container-fluid {
	list-style: none;
	border-top: 1px solid #000;
	padding-top: 5px;
	padding-left: 45px;
}
#coupons {
	margin-bottom: 30px;
}
.form-cart-loading {
	position: fixed;
	z-index: 99999;
	width: 100%;
	height: 100%;
	left: 0;
	top: 0;
	background-color: rgba(25, 25, 25, 0.4);
	text-align: center;
}
.form-cart-spinner-icon {
	margin-top: 25%;
}
/* hide the coupon input when coupons are disabled */
#shopping-cart[data-couponlimit="0"] #coupons{
	display: none;
}

.visually-hidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap; /* Optional: Prevent line breaks */
}
</style>